// Deterministic unit conversion.
//
// Same-dimension conversions (volume/weight/count) are always available.
// Cross-dimension conversions (volume<->weight<->count) need the ingredient
// name + a known density / piece-weight and are routed through grams. Unknown
// units/ingredients return null and callers fall back to no conversion.

const VOLUME = {
    ml: 1, l: 1000, tsp: 4.92892, tbsp: 14.7868, cup: 240,
    floz: 29.5735, pint: 473.176, quart: 946.353, gallon: 3785.41,
};
const WEIGHT = { mg: 0.001, g: 1, kg: 1000, oz: 28.3495, lb: 453.592 };
const COUNT = { unit: 1, piece: 1, clove: 1, dozen: 12 };

// grams per millilitre, by ingredient core name (longest key matched first).
export const DEFAULT_DENSITY_G_PER_ML = {
    'olive oil': 0.92, 'vegetable oil': 0.92, 'coconut oil': 0.92,
    'brown sugar': 0.90, 'icing sugar': 0.56, 'powdered sugar': 0.56,
    'soy sauce': 1.15, 'fish sauce': 1.20, 'maple syrup': 1.37,
    'condensed milk': 1.29, water: 1.00, milk: 1.03, cream: 1.01,
    yogurt: 1.03, oil: 0.92, butter: 0.96, flour: 0.53, sugar: 0.85,
    salt: 1.22, rice: 0.85, honey: 1.42, syrup: 1.37, vinegar: 1.01,
    cocoa: 0.52, cornstarch: 0.54, cornflour: 0.54, oats: 0.41,
    breadcrumbs: 0.35, ketchup: 1.14, mayonnaise: 0.91,
};

// grams per single piece, by ingredient core name.
export const DEFAULT_GRAMS_PER_PIECE = {
    'chicken breast': 175, 'whole chicken': 1500, chicken: 1500, egg: 50,
    garlic: 5, clove: 5, onion: 110, tomato: 120, potato: 170, carrot: 60,
    lemon: 100, lime: 67, apple: 180, banana: 120, ginger: 30,
};

// Effective tables = defaults merged with admin overrides (loaded from
// Firestore via loadConversionOverrides). Start with the built-in defaults so
// conversion works before/without a load.
let DENSITY_G_PER_ML = { ...DEFAULT_DENSITY_G_PER_ML };
let GRAMS_PER_PIECE = { ...DEFAULT_GRAMS_PER_PIECE };

// Load admin-managed overrides from Firestore and merge over the defaults.
// Safe to call repeatedly; failures leave the defaults in place.
export async function loadConversionOverrides(db) {
    try {
        const [{ getDocs, collection }] = await Promise.all([
            import('@firebase/firestore'),
        ]);
        const density = { ...DEFAULT_DENSITY_G_PER_ML };
        const dSnap = await getDocs(collection(db, 'ingredientDensities'));
        dSnap.forEach((d) => {
            const { name, gPerMl } = d.data();
            const n = String(name || '').toLowerCase().trim();
            const v = parseFloat(gPerMl);
            if (n && Number.isFinite(v) && v > 0) density[n] = v;
        });
        DENSITY_G_PER_ML = density;

        const piece = { ...DEFAULT_GRAMS_PER_PIECE };
        const pSnap = await getDocs(collection(db, 'ingredientPieceWeights'));
        pSnap.forEach((d) => {
            const { name, gramsPerPiece } = d.data();
            const n = String(name || '').toLowerCase().trim();
            const v = parseFloat(gramsPerPiece);
            if (n && Number.isFinite(v) && v > 0) piece[n] = v;
        });
        GRAMS_PER_PIECE = piece;
    } catch (e) {
        // Keep defaults on any failure.
        console.log('loadConversionOverrides failed; using defaults', e);
    }
}

const ALIASES = {
    teaspoon: 'tsp', teaspoons: 'tsp', tsps: 'tsp',
    tablespoon: 'tbsp', tablespoons: 'tbsp', tbsps: 'tbsp', tbs: 'tbsp',
    cups: 'cup', c: 'cup',
    milliliter: 'ml', milliliters: 'ml', millilitre: 'ml', millilitres: 'ml', mls: 'ml',
    liter: 'l', liters: 'l', litre: 'l', litres: 'l',
    gram: 'g', grams: 'g', gr: 'g', gs: 'g',
    kilogram: 'kg', kilograms: 'kg', kgs: 'kg',
    milligram: 'mg', milligrams: 'mg',
    ounce: 'oz', ounces: 'oz', ozs: 'oz',
    pound: 'lb', pounds: 'lb', lbs: 'lb',
    'fluid ounce': 'floz', 'fluid ounces': 'floz', 'fl oz': 'floz',
    pints: 'pint', pt: 'pint',
    quarts: 'quart', qt: 'quart',
    gallons: 'gallon', gal: 'gallon',
    units: 'unit', pieces: 'piece', pcs: 'piece', pc: 'piece',
    cloves: 'clove', dozens: 'dozen',
};

function normalizeUnit(unit) {
    const u = String(unit || '').toLowerCase().trim().replace(/\./g, '');
    return ALIASES[u] || u;
}

function dimensionFor(key) {
    if (Object.prototype.hasOwnProperty.call(VOLUME, key)) return VOLUME;
    if (Object.prototype.hasOwnProperty.call(WEIGHT, key)) return WEIGHT;
    if (Object.prototype.hasOwnProperty.call(COUNT, key)) return COUNT;
    return null;
}

function lookupByIngredient(table, ingredient) {
    const name = String(ingredient || '').toLowerCase().trim();
    if (!name) return null;
    const keys = Object.keys(table).sort((a, b) => b.length - a.length);
    for (let i = 0; i < keys.length; i += 1) {
        if (name === keys[i] || name.includes(keys[i])) return table[keys[i]];
    }
    return null;
}

function toGrams(qty, unit, ingredient) {
    if (Object.prototype.hasOwnProperty.call(WEIGHT, unit)) return qty * WEIGHT[unit];
    if (Object.prototype.hasOwnProperty.call(VOLUME, unit)) {
        const d = lookupByIngredient(DENSITY_G_PER_ML, ingredient);
        if (d === null) return null;
        return qty * VOLUME[unit] * d;
    }
    if (Object.prototype.hasOwnProperty.call(COUNT, unit)) {
        const w = lookupByIngredient(GRAMS_PER_PIECE, ingredient);
        if (w === null) return null;
        return qty * COUNT[unit] * w;
    }
    return null;
}

function fromGrams(grams, unit, ingredient) {
    if (Object.prototype.hasOwnProperty.call(WEIGHT, unit)) return grams / WEIGHT[unit];
    if (Object.prototype.hasOwnProperty.call(VOLUME, unit)) {
        const d = lookupByIngredient(DENSITY_G_PER_ML, ingredient);
        if (d === null) return null;
        return grams / d / VOLUME[unit];
    }
    if (Object.prototype.hasOwnProperty.call(COUNT, unit)) {
        const w = lookupByIngredient(GRAMS_PER_PIECE, ingredient);
        if (w === null) return null;
        return grams / w / COUNT[unit];
    }
    return null;
}

// Convert qty from fromUnit to toUnit. null if not convertible.
export function convertUnit(qty, fromUnit, toUnit, ingredient = '') {
    const f = normalizeUnit(fromUnit);
    const t = normalizeUnit(toUnit);
    if (f === t) return qty;
    const fromDim = dimensionFor(f);
    const toDim = dimensionFor(t);
    if (!fromDim || !toDim) return null;
    if (fromDim === toDim) return (qty * fromDim[f]) / toDim[t];
    const grams = toGrams(qty, f, ingredient);
    if (grams === null) return null;
    return fromGrams(grams, t, ingredient);
}

// Amount of pantryUnit consumed by needing ingredientQty ingredientUnit.
// Falls back to the raw ingredient qty when not convertible.
export function amountToDeduct(ingredientQty, ingredientUnit, pantryUnit, ingredient = '') {
    const converted = convertUnit(ingredientQty, ingredientUnit, pantryUnit, ingredient);
    const amount = converted === null ? ingredientQty : converted;
    return Math.max(0, amount);
}
